import { useEffect, useState } from "react";
import {
  ArrowDownRight,
  ArrowRight,
  Bot,
  Check,
  ChevronRight,
  CircleDollarSign,
  Coins,
  Copy,
  ExternalLink,
  Flame,
  Gauge,
  KeyRound,
  Layers3,
  LockKeyhole,
  PauseCircle,
  RefreshCw,
  Route,
  ShieldCheck,
  Sparkles,
  TimerReset,
  Vault,
  WalletCards,
  Zap
} from "lucide-react";

const TELEGRAM_URL = "https://t.me/hoodballbot";
const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
const TOKEN_ADDRESS = "0x396d0362E45b31E50ee896804BCbfdeA48daFAaF";

interface HoodballStatsSnapshot {
  totalEthClaimed: string;
  hoodball: {
    configured: boolean;
    tokenAddress: string | null;
    priceUsd: number | null;
    burnedValueUsd: number | null;
    boughtTokens: string | null;
    buybackWethSpent: string | null;
    burnedTokens: string | null;
    burnedPercent: number | null;
  };
  updatedAt: string;
}

const launchpads = [
  { name: "Lunch", detail: "Launch + automate", tone: "lime" },
  { name: "Noxa.fun", detail: "Fee routing", tone: "ember" },
  { name: "Pons", detail: "Fee redirect", tone: "lime" },
  { name: "Hood.fun", detail: "Post-graduation", tone: "ember" },
  { name: "Klik", detail: "V4 route", tone: "lime" },
  { name: "Clanker", detail: "Reward delegation", tone: "ember" },
  { name: "Real.fun", detail: "BAGS mode", tone: "lime" }
] as const;

const routeInputs = [
  { icon: CircleDollarSign, title: "Fee recipient", detail: "Protocol payouts arrive directly in the token vault", examples: "Noxa.fun · Pons" },
  { icon: Sparkles, title: "Creator rewards", detail: "Delegated rewards are collected on the creator schedule", examples: "Clanker · Klik" },
  { icon: Route, title: "Post-graduation", detail: "Trading fees continue after the token leaves its curve", examples: "Hood.fun · Real.fun" },
  { icon: Layers3, title: "Native launch", detail: "A new token starts with Hoodball routing already configured", examples: "Lunch" }
] as const;

const ecosystemWatchlist = [
  "Flap", "Flap PVE", "Trench", "Bow.fun", "Stoxes", "Holoworld", "Bags", "Livo", "Virtuals",
  "Bankr", "Printr", "DYOR.fun", "DYOR.fun V3", "Ape.store", "LaunchHood", "Coinbarrel", "LeaveHood", "Robinfun"
] as const;

const steps = [
  {
    number: "01",
    icon: Layers3,
    title: "Launch or import",
    body: "Launch a new token through Lunch, or paste the address of an existing supported token."
  },
  {
    number: "02",
    icon: Route,
    title: "Connect fee rights",
    body: "Hoodball creates an isolated vault and verifies the onchain route. Most creators never need to import a wallet."
  },
  {
    number: "03",
    icon: RefreshCw,
    title: "Claim & buy back",
    body: "At your chosen interval, the bot claims fees and buys the same token that generated them."
  },
  {
    number: "04",
    icon: Flame,
    title: "Burn. Reconcile. Repeat.",
    body: "Bought tokens move to the dead address. Each cycle is recorded, reconciled and safely retried when needed."
  }
] as const;

const safeguards = [
  { icon: Vault, title: "One vault per token", body: "Fees, balances, gas and nonces stay isolated between automations." },
  { icon: KeyRound, title: "Separated signer", body: "The bot requests signatures without returning private keys to the application process." },
  { icon: ShieldCheck, title: "Contract checks", body: "Pinned bytecode and proxy implementations are checked before a supported adapter signs." },
  { icon: TimerReset, title: "Idempotent cycles", body: "Distributed locks and serialized nonces prevent duplicate claims and overlapping jobs." },
  { icon: Gauge, title: "Gas-aware pauses", body: "A token pauses before its vault drops below the configured gas reserve." },
  { icon: PauseCircle, title: "Funds stay put on failure", body: "If a safe quote disappears or a swap reverts, assets remain in the vault for a later retry." }
] as const;

function App() {
  const [contractCopied, setContractCopied] = useState(false);
  const [hoodballStats, setHoodballStats] = useState<HoodballStatsSnapshot | null>(null);
  const [statsError, setStatsError] = useState(false);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch("/api/hoodball-stats", { headers: { accept: "application/json" }, cache: "no-store" });
        if (!response.ok) throw new Error(`stats_http_${response.status}`);
        const snapshot = await response.json() as HoodballStatsSnapshot;
        if (snapshot.hoodball.tokenAddress?.toLowerCase() !== TOKEN_ADDRESS.toLowerCase()) throw new Error("stats_token_mismatch");
        if (active) {
          setHoodballStats(snapshot);
          setStatsError(false);
        }
      } catch {
        if (active) setStatsError(true);
      }
    };
    void refresh();
    const interval = window.setInterval(refresh, 60_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  const copyContract = async () => {
    try {
      await navigator.clipboard.writeText(TOKEN_ADDRESS);
    } catch {
      const input = document.createElement("textarea");
      input.value = TOKEN_ADDRESS;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setContractCopied(true);
    window.setTimeout(() => setContractCopied(false), 1800);
  };

  return (
    <div className="app-shell">
      <header className="hero" id="top">
        <nav className="nav container" aria-label="Primary navigation">
          <a className="brand" href="#top" aria-label="Hoodball home">
            <img src="/brand-mark.webp" alt="" className="brand-mark" width="512" height="512" />
            <span>HOODBALL</span>
          </a>

          <div className="nav-links">
            <a href="#how">How it works</a>
            <a href="#launchpads">Launchpads</a>
            <a href="#token">$HOODBALL</a>
            <a href="#security">Security</a>
            <a href="#faq">FAQ</a>
          </div>

          <a className="button button-small button-light" href={TELEGRAM_URL} target="_blank" rel="noreferrer">
            Open Telegram <ArrowUpRightIcon />
          </a>
        </nav>

        <img
          className="hero-art"
          src="/images/hoodball-hero.webp"
          alt="Hoodball's hooded mascot moving through an onchain fee engine"
          width="1536"
          height="1024"
          fetchPriority="high"
        />
        <div className="hero-wash" />
        <div className="hero-grid" />

        <div className="hero-content container">
          <div className="eyebrow"><span className="pulse-dot" /> Automated buybacks on Robinhood Chain</div>
          <h1>Creator fees in.<br /><em>Tokens burned.</em></h1>
          <p className="hero-copy">
            Hoodball claims creator fees, buys back the token that earned them, and sends every purchased token to the burn address—automatically.
          </p>
          <div className="hero-actions">
            <a className="button button-primary" href={TELEGRAM_URL} target="_blank" rel="noreferrer">
              Open Hoodball <span className="button-orb"><ArrowRight size={19} /></span>
            </a>
            <a className="text-link" href="#how">See the engine <ArrowDownRight size={18} /></a>
          </div>
          <div className="hero-proof">
            <div><strong>4663</strong><span>Chain ID</span></div>
            <div><strong>100%</strong><span>Buyback & burn</span></div>
            <div><strong>24/7</strong><span>Automation</span></div>
          </div>
        </div>

        <div className="hero-rail" aria-hidden="true">
          <span>CLAIM</span><ChevronRight size={14} /><span>BUY</span><ChevronRight size={14} /><span>BURN</span>
        </div>
      </header>

      <main>
        <section className="marquee-section" aria-label="Live Hoodball launchpad routes">
          <div className="marquee-fade marquee-fade-left" />
          <div className="marquee-track">
            {[...launchpads, ...launchpads].map((launchpad, index) => (
              <div className="marquee-item" key={`${launchpad.name}-${index}`}>
                <span className={`status-dot ${launchpad.tone}`} />
                <strong>{launchpad.name}</strong>
                <span>{launchpad.detail}</span>
              </div>
            ))}
          </div>
          <div className="marquee-fade marquee-fade-right" />
        </section>

        <section className="token-section section" id="token">
          <div className="container">
            <div className="section-heading split-heading">
              <div>
                <div className="eyebrow eyebrow-dark"><span className="status-pill">CONTRACT LIVE</span> $HOODBALL</div>
                <h2>Official contract.<br />One source of truth.</h2>
              </div>
              <div className="heading-copy">
                <p>The verified contract is published below. Finalized Hoodball buybacks and the onchain burn balance update automatically every minute.</p>
                <span className="quiet-note"><LockKeyhole size={15} /> Always verify the full address before interacting.</span>
              </div>
            </div>

            <div className="terminal-card">
              <div className="terminal-topline">
                <div className="token-identity">
                  <img src="/brand-mark.webp" alt="" width="512" height="512" />
                  <div><strong>$HOODBALL</strong><span>Contract verified</span></div>
                </div>
                <div className={`terminal-live${hoodballStats ? " is-live" : statsError ? " is-error" : ""}`} aria-live="polite">
                  <span /> {statsStatus(hoodballStats, statsError)}
                </div>
              </div>

              <div className="contract-live contract-live-top">
                <div>
                  <span>OFFICIAL CONTRACT</span>
                  <a href={`https://robinhoodchain.blockscout.com/token/${TOKEN_ADDRESS}`} target="_blank" rel="noreferrer" title={TOKEN_ADDRESS}>
                    {TOKEN_ADDRESS}<ExternalLink size={12} />
                  </a>
                </div>
                <button type="button" onClick={copyContract} aria-label="Copy the official HOODBALL contract address">
                  {contractCopied ? <Check size={14} /> : <Copy size={14} />}
                  <span aria-live="polite">{contractCopied ? "COPIED" : "COPY"}</span>
                </button>
              </div>

              <div className="terminal-grid">
                <div className="price-panel">
                  <div className="price-copy">
                    <span>PRICE / USD</span>
                    <strong>{formatUsdPrice(hoodballStats?.hoodball.priceUsd)}</strong>
                    <p>Current price from the highest-liquidity $HOODBALL market on Robinhood Chain.</p>
                    <a href={`https://dexscreener.com/robinhood/${TOKEN_ADDRESS}`} target="_blank" rel="noreferrer">
                      LIVE ON DEXSCREENER <ExternalLink size={12} />
                    </a>
                  </div>
                  <div className="price-orbit" aria-hidden="true"><i /><i /><i /></div>
                </div>

                <div className="metric-panel metric-panel-compact">
                  <Metric label="Supply permanently burned" value={formatBurnPercent(hoodballStats?.hoodball.burnedPercent)} secondary={`${formatUsdValue(hoodballStats?.hoodball.burnedValueUsd)} burned value`} detail="Verified onchain at 0x…dEaD" featured />
                  <Metric label="Total ETH claimed" value={formatEth(hoodballStats?.totalEthClaimed)} detail="ETH + WETH from all confirmed token claims" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section process-section" id="how">
          <div className="container">
            <div className="section-heading process-heading">
              <div>
                <div className="eyebrow eyebrow-dark">The autonomous loop</div>
                <h2>Four moves.<br />One outcome.</h2>
              </div>
              <p>Choose an interval from 30 minutes to 24 hours. Hoodball keeps the execution narrow, observable and tied to the token that produced the fees.</p>
            </div>

            <div className="cycle-visual" aria-label="Hoodball automation cycle">
              <CycleNode icon={CircleDollarSign} label="Creator fees" index="01" />
              <FlowLine label="ROUTE" />
              <CycleNode icon={WalletCards} label="Isolated vault" index="02" />
              <FlowLine label="SWAP" />
              <CycleNode icon={Coins} label="Same token" index="03" />
              <FlowLine label="BURN" hot />
              <CycleNode icon={Flame} label="0x…dEaD" index="04" hot />
            </div>

            <div className="steps-grid">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <article className="step-card" key={step.number}>
                    <div className="step-top"><span>{step.number}</span><Icon size={22} /></div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </article>
                );
              })}
            </div>

            <div className="gas-note"><Zap size={18} /> Keep each token vault funded with ETH for automation gas. Creator fees are never used to pay gas.</div>
          </div>
        </section>

        <section className="section launchpads-section" id="launchpads">
          <div className="container">
            <div className="section-heading split-heading light-heading">
              <div>
                <div className="eyebrow">Multi-launchpad routing</div>
                <h2>Different fees.<br />One burn engine.</h2>
              </div>
              <p className="heading-copy">Every launchpad handles creator fees differently. Hoodball turns those paths into one simple loop—collect, buy back and burn—while adding new adapters as the ecosystem grows.</p>
            </div>

            <div className="routing-stage">
              <div className="route-inputs">
                <div className="routing-label"><span>01</span> FEE SOURCES</div>
                {routeInputs.map((input) => {
                  const Icon = input.icon;
                  return (
                    <article className="route-input" key={input.title}>
                      <div className="route-input-icon"><Icon size={18} /></div>
                      <div><h3>{input.title}</h3><p>{input.detail}</p><span>{input.examples}</span></div>
                      <i aria-hidden="true" />
                    </article>
                  );
                })}
              </div>

              <div className="router-hub" aria-label="Verified launchpad fees converge in the Hoodball routing engine">
                <div className="routing-label"><span>02</span> VERIFY & ROUTE</div>
                <div className="router-orbit router-orbit-outer"><i /><i /><i /></div>
                <div className="router-orbit router-orbit-inner" />
                <div className="router-core">
                  <img src="/brand-mark.webp" alt="" width="512" height="512" />
                  <strong>HOODBALL</strong>
                  <span>ADAPTER ENGINE</span>
                </div>
                <div className="router-check"><Check size={13} /> RIGHTS & CONTRACT VERIFIED</div>
              </div>

              <article className="burn-output">
                <div className="routing-label"><span>03</span> AUTOMATED OUTPUT</div>
                <div className="burn-output-icon"><Flame size={31} /></div>
                <div>
                  <span className="burn-kicker">THE SAME DESTINATION</span>
                  <h3>Claim.<br />Buy back.<br /><em>Burn.</em></h3>
                  <p>Whatever the fee source, purchased tokens finish at the permanent dead address.</p>
                </div>
                <code>0x0000…dEaD</code>
              </article>
            </div>

            <div className="coverage-board">
              <div className="coverage-row coverage-live">
                <div className="coverage-title"><span className="live-pulse" /><div><strong>Live routes</strong><small>Available in Hoodball now</small></div></div>
                <div className="coverage-tags">
                  {launchpads.map((item) => <span key={item.name}>{item.name}<Check size={11} /></span>)}
                </div>
              </div>
              <div className="coverage-row coverage-watch">
                <div className="coverage-title"><Route size={16} /><div><strong>Ecosystem expansion</strong><small>Adapters evaluated as fee rights become verifiable</small></div></div>
                <div className="coverage-tags">
                  {ecosystemWatchlist.map((item) => <span key={item}>{item}</span>)}
                </div>
              </div>
            </div>
            <p className="support-footnote">Only launchpads marked as live can currently be activated. Other names indicate integration coverage being evaluated, not endorsement or partnership.</p>
          </div>
        </section>

        <section className="section security-section" id="security">
          <div className="container">
            <div className="security-intro">
              <div className="eyebrow eyebrow-dark">Operational safeguards</div>
              <h2>Automation with<br /><span>guardrails.</span></h2>
              <p>Financial automation should fail visibly, preserve funds and avoid unsafe fallback routes. Hoodball is designed around those constraints.</p>
            </div>

            <div className="safeguards-grid">
              {safeguards.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="safeguard-card" key={item.title}>
                    <div className="icon-box"><Icon size={21} /></div>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </article>
                );
              })}
            </div>

            <div className="custody-callout">
              <div><Bot size={26} /><strong>Custodial beta</strong></div>
              <p>Use dedicated wallets only. Never send a private key in a group chat, and do not use an imported creator wallet for trading or personal balances.</p>
            </div>
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <div className="container faq-layout">
            <div className="faq-title">
              <div className="eyebrow eyebrow-dark">Straight answers</div>
              <h2>Before you<br />start the loop.</h2>
              <a className="text-link dark-link" href={TELEGRAM_URL} target="_blank" rel="noreferrer">Ask Hoodball <ExternalLink size={16} /></a>
            </div>
            <div className="faq-list">
              <Faq question="Is Hoodball custodial?">Yes. Hoodball controls isolated execution wallets so it can claim, swap and burn on schedule. Use dedicated wallets and keep only the balances required for each automation.</Faq>
              <Faq question="Do I have to import my creator wallet?">Usually not. When the launchpad supports fee delegation, Hoodball creates a vault and asks you to set it as the 100% payout recipient. Import is only offered for routes that require the creator wallet itself.</Faq>
              <Faq question="Which launchpads are supported?">Lunch, Noxa.fun, Pons, Hood.fun after graduation, Klik, Clanker and Real.fun in BAGS mode. Token versions and fee rights are verified individually.</Faq>
              <Faq question="How does Noxa work?">The creator routes the WETH payout to the token's Hoodball vault. Noxa burns the token side during collection, then Hoodball uses the received WETH to buy and burn additional tokens.</Faq>
              <Faq question="Can Hoodball launch a new token?">Yes, through Lunch. Existing supported tokens from other launchpads can be imported and automated, but Hoodball does not launch through those platforms.</Faq>
              <Faq question="Who pays gas?">The user funds each token's vault with ETH. Hoodball keeps gas separate from creator fees and pauses automation when the reserve becomes too low.</Faq>
              <Faq question="What is the official $HOODBALL contract?">The verified contract is 0x396d0362E45b31E50ee896804BCbfdeA48daFAaF. Copy it only from the official contract panel above.</Faq>
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="final-glow" />
          <div className="container final-cta-inner">
            <div className="eyebrow"><Sparkles size={15} /> Put creator fees back to work</div>
            <h2>Start the burn loop.</h2>
            <p>Launch with Lunch or connect an existing supported token in Telegram.</p>
            <a className="button button-primary" href={TELEGRAM_URL} target="_blank" rel="noreferrer">
              Open @hoodballbot <span className="button-orb"><ArrowRight size={19} /></span>
            </a>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-grid">
          <div className="footer-brand">
            <a className="brand" href="#top"><img src="/brand-mark.webp" alt="" className="brand-mark" width="512" height="512" /><span>HOODBALL</span></a>
            <p>Automated creator-fee buybacks and burns on Robinhood Chain.</p>
          </div>
          <div className="footer-links">
            <a href={TELEGRAM_URL} target="_blank" rel="noreferrer">Telegram</a>
            <a href={`https://robinhoodchain.blockscout.com/address/${BURN_ADDRESS}`} target="_blank" rel="noreferrer">Burn address</a>
          </div>
          <p className="footer-risk">Hoodball is custodial beta software. Crypto assets are risky. No automated system can guarantee uninterrupted operation or financial outcomes.</p>
        </div>
        <div className="container footer-bottom"><span>© 2026 Hoodball</span><span>Robinhood Chain · 4663</span></div>
      </footer>
    </div>
  );
}

function Metric({ label, value, secondary, detail, featured = false }: { label: string; value: string; secondary?: string; detail?: string; featured?: boolean }) {
  return <div className={`metric${featured ? " metric-featured" : ""}`}><span>{label}</span><strong>{value}</strong>{secondary ? <b>{secondary}</b> : null}{detail ? <small>{detail}</small> : null}</div>;
}

function formatUsdPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const fractionDigits = value >= 1 ? 4 : value >= 0.01 ? 6 : 8;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(value);
}

function formatBurnPercent(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toFixed(4)}%`;
}

function formatUsdValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(value);
}

function formatEth(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 }).format(parsed)} ETH`;
}

function statsStatus(stats: HoodballStatsSnapshot | null, failed: boolean): string {
  if (stats) {
    const updated = new Date(stats.updatedAt);
    return `Onchain stats · ${updated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return failed ? "Stats temporarily unavailable" : "Loading onchain stats…";
}

function CycleNode({ icon: Icon, label, index, hot = false }: { icon: typeof Coins; label: string; index: string; hot?: boolean }) {
  return (
    <div className={`cycle-node${hot ? " hot" : ""}`}>
      <span className="cycle-index">{index}</span>
      <div className="cycle-icon"><Icon size={25} /></div>
      <strong>{label}</strong>
    </div>
  );
}

function FlowLine({ label, hot = false }: { label: string; hot?: boolean }) {
  return <div className={`flow-line${hot ? " hot" : ""}`}><span>{label}</span><i /></div>;
}

function Faq({ question, children }: { question: string; children: string }) {
  return (
    <details>
      <summary><span>{question}</span><span className="faq-plus">+</span></summary>
      <p>{children}</p>
    </details>
  );
}

function ArrowUpRightIcon() {
  return <ExternalLink size={15} aria-hidden="true" />;
}

export default App;
